import { useEffect, useState } from 'react';
import { remarketingTracker, UserSegment } from '../../lib/remarketing';

const RemarketingSegments = () => {
  const [segment, setSegment] = useState<UserSegment>(UserSegment.NEW_VISITOR);
  const [behaviorData, setBehaviorData] = useState<any>(null);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    // Update segment on mount
    const currentSegment = remarketingTracker.getUserSegment();
    setSegment(currentSegment);
    
    const data = remarketingTracker.getBehaviorData();
    setBehaviorData(data);

    // Update custom audiences
    remarketingTracker.updateCustomAudiences();

    // Update segment every 30 seconds
    const interval = setInterval(() => {
      const newSegment = remarketingTracker.getUserSegment();
      setSegment(newSegment);
      remarketingTracker.updateCustomAudiences();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getSegmentInfo = (seg: UserSegment) => {
    switch (seg) {
      case UserSegment.NEW_VISITOR:
        return {
          label: 'Nový návštěvník',
          color: 'bg-blue-100 text-blue-800',
          icon: 'ri-user-add-line'
        };
      case UserSegment.SERVICE_VIEWER:
        return {
          label: 'Prohlíží služby',
          color: 'bg-purple-100 text-purple-800',
          icon: 'ri-eye-line'
        };
      case UserSegment.CART_ABANDONER:
        return {
          label: 'Opustil košík',
          color: 'bg-orange-100 text-orange-800',
          icon: 'ri-shopping-cart-line'
        };
      case UserSegment.FREQUENT_CUSTOMER:
        return {
          label: 'Stálý zákazník',
          color: 'bg-green-100 text-green-800',
          icon: 'ri-vip-crown-line'
        };
      case UserSegment.HIGH_VALUE:
        return {
          label: 'VIP zákazník',
          color: 'bg-yellow-100 text-yellow-800',
          icon: 'ri-star-line'
        };
      case UserSegment.INACTIVE:
        return {
          label: 'Neaktivní',
          color: 'bg-gray-100 text-gray-800',
          icon: 'ri-time-line'
        };
      default:
        return {
          label: 'Neznámý',
          color: 'bg-gray-100 text-gray-800',
          icon: 'ri-question-line'
        };
    }
  };

  const segmentInfo = getSegmentInfo(segment);

  // Only show in development mode
  if (import.meta.env.PROD && !showDebug) {
    return null;
  }

  return (
    <>
      {/* Debug Toggle Button (only in dev) */}
      {import.meta.env.DEV && (
        <button
          onClick={() => setShowDebug(!showDebug)}
          className="fixed bottom-24 right-6 z-50 w-12 h-12 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
          title="Zobrazit remarketing debug"
        >
          <i className="ri-bar-chart-box-line text-xl"></i>
        </button>
      )}

      {/* Debug Panel */}
      {showDebug && (
        <div className="fixed bottom-40 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-lg">Remarketing Debug</h3>
              <button
                onClick={() => setShowDebug(false)}
                className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
            {/* Current Segment */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">
                Aktuální segment
              </label>
              <div className={`${segmentInfo.color} rounded-lg p-3 flex items-center gap-3`}>
                <i className={`${segmentInfo.icon} text-2xl`}></i>
                <span className="font-semibold">{segmentInfo.label}</span>
              </div>
            </div>

            {/* Behavior Stats */}
            {behaviorData && (
              <>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">
                    Statistiky chování
                  </label>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Počet návštěv:</span>
                      <span className="font-semibold text-gray-900">{behaviorData.visitCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Prohlédnuté služby:</span>
                      <span className="font-semibold text-gray-900">{behaviorData.viewedServices.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">V košíku:</span>
                      <span className="font-semibold text-gray-900">{behaviorData.addedToCart.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Dokončené objednávky:</span>
                      <span className="font-semibold text-gray-900">{behaviorData.completedOrders}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Celková útrata:</span>
                      <span className="font-semibold text-teal-600">{behaviorData.totalSpent} Kč</span>
                    </div>
                  </div>
                </div>

                {/* Recent Views */}
                {behaviorData.viewedServices.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">
                      Naposledy prohlédnuté
                    </label>
                    <div className="space-y-1">
                      {behaviorData.viewedServices.slice(-3).reverse().map((service: any, index: number) => (
                        <div key={index} className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                          {service.serviceName} - {service.price} Kč
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Clear Data Button */}
            <button
              onClick={() => {
                remarketingTracker.clearBehavior();
                window.location.reload();
              }}
              className="w-full bg-red-50 text-red-600 rounded-lg py-2 px-4 text-sm font-semibold hover:bg-red-100 transition-colors"
            >
              <i className="ri-delete-bin-line mr-2"></i>
              Vymazat data
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RemarketingSegments;
